/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from '@angular/fire/auth';
import { from, Observable, throwError } from 'rxjs';
import { catchError, first, switchMap } from 'rxjs/operators';
import { WindowService } from 'src/app/shared/services/window/window.service';
import { AuthService } from '../services/auth/auth.service';
import { environment } from 'src/environments/environment';
import { BRAND_PACK_CACHE_KEY } from '../services/brand-pack/brand-pack.service';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private _authService: AuthService,
    private _windowService: WindowService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this._authService.getIdToken$().pipe(
      first(),
      switchMap(user => {
        if (user) {
          return from(user.getIdToken()).pipe(
            switchMap((token: string) => {
              if (token) {
                req = this.addTokenHeader(req, token);
              }
              return next.handle(req).pipe(
                catchError(error => {
                  if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.handle401(req, next, user);
                  } else {
                    return throwError(() => error);
                  }
                })
              );
            })
          );
        }
        return this._authService.unauthenticate().pipe(
          switchMap(() => {
            // A full-page logout, like the sidebar's: drop the remembered brand pack so it
            // can't outlive the session (MP-11488). Storage is cleared directly, because
            // injecting BrandPackService here would loop through HttpClient.
            try {
              localStorage.removeItem(BRAND_PACK_CACHE_KEY);
            } catch {
              // Storage unavailable; nothing was cached.
            }
            this._windowService.open(`${environment.logoutRoute}&reason=notoken`, '_self');
            return next.handle(req);
          })
        );
      })
    );
  }

  /**
   * Retries a 401'd request with a freshly forced token. The refresh is shared
   * by the service, so N requests failing on the same expired token cause one
   * refresh, not N.
   */
  private handle401(request: HttpRequest<any>, next: HttpHandler, user: User) {
    return this._authService.refreshIdToken$(user).pipe(
      switchMap((token: string) => {
        return next.handle(token ? this.addTokenHeader(request, token) : request);
      })
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    request = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
    return request;
  }
}
